import { Link } from "react-router-dom";
import { Sparkle, ChartBar, FileText, Infinity as InfinityIcon, Lock } from "@phosphor-icons/react";

/**
 * Displays quota usage for a subscription status payload.
 * variant:
 *  - "compact"  → single-row inline (used in Dashboard tile)
 *  - "detailed" → 4-tile grid (used in /billing)
 */
export default function UsageWidget({ status, variant = "compact" }) {
  if (!status) return null;
  const limits = status.limits || {};
  const usage = status.usage || status.usage_this_month || {};

  const items = [
    {
      key: "assessments",
      label: "Assessments",
      icon: ChartBar,
      used: Number(usage.assessments || 0),
      limit: limits.assessments_per_month,
      color: "text-teal",
      bg: "bg-teal-soft",
    },
    {
      key: "investment_docs",
      label: "Investment Docs",
      icon: FileText,
      used: Number(usage.investment_docs || 0),
      limit: limits.investment_docs_per_month,
      color: "text-gold",
      bg: "bg-gold-soft",
    },
  ];

  if (variant === "compact") {
    return (
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground" data-testid="usage-widget-compact">
        {items.map((it) => (
          <UsageInline key={it.key} item={it} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="usage-widget-detailed">
      {items.map((it) => (
        <UsageTile key={it.key} item={it} />
      ))}
      {/* Boolean features */}
      <BoolTile
        label="Report exports"
        detail="PDF · DOCX · PPTX"
        enabled={Boolean(limits.exports)}
      />
      <BoolTile
        label="Advanced agents"
        detail="Regulatory · Risk · ESG · Valuation"
        enabled={Boolean(limits.advanced_agents)}
      />
    </div>
  );
}

function pctOf(used, limit) {
  if (limit == null || limit === 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function UsageInline({ item }) {
  const { label, used, limit } = item;
  if (limit == null) return <span data-testid={`usage-inline-${item.key}`}>{label}: <span className="text-foreground font-semibold">Unlimited</span></span>;
  if (limit === 0) return <span data-testid={`usage-inline-${item.key}`}>{label}: <span className="text-foreground font-semibold">Not included</span></span>;
  return (
    <span data-testid={`usage-inline-${item.key}`}>
      {label}: <span className="text-foreground font-semibold">{used}/{limit}</span>
    </span>
  );
}

function UsageTile({ item }) {
  const { label, icon: Icon, used, limit, color, bg } = item;
  const unlimited = limit == null;
  const notIncluded = limit === 0;
  const pct = pctOf(used, limit);
  const remaining = unlimited ? null : Math.max(0, (limit || 0) - used);
  const nearLimit = !unlimited && !notIncluded && pct >= 80;

  return (
    <div className="rounded-xl border border-border p-5 bg-background" data-testid={`usage-tile-${item.key}`}>
      <div className="flex items-center justify-between">
        <div className={`h-8 w-8 rounded-lg grid place-items-center ${bg} ${color}`}>
          <Icon size={16} weight="bold" />
        </div>
        {unlimited ? (
          <InfinityIcon size={16} weight="bold" className="text-muted-foreground" />
        ) : notIncluded ? (
          <Lock size={14} weight="bold" className="text-muted-foreground" />
        ) : null}
      </div>
      <div className="mt-3 text-overline text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        {unlimited ? (
          <div className="data-large text-2xl">Unlimited</div>
        ) : notIncluded ? (
          <div className="data-large text-lg text-muted-foreground">Not included</div>
        ) : (
          <>
            <div className="data-large text-3xl">{used}</div>
            <div className="text-sm text-muted-foreground">/ {limit}</div>
          </>
        )}
      </div>
      {!unlimited && !notIncluded && (
        <>
          <div className="mt-3 h-1.5 bg-secondary rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${nearLimit ? "bg-amber-500" : "bg-primary"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {remaining} remaining this period
          </div>
        </>
      )}
      {notIncluded && (
        <Link to="/pricing?plan=starter" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`usage-tile-${item.key}-upgrade`}>
          Upgrade to unlock →
        </Link>
      )}
    </div>
  );
}

function BoolTile({ label, detail, enabled }) {
  return (
    <div className="rounded-xl border border-border p-5 bg-background" data-testid={`usage-bool-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className={`h-8 w-8 rounded-lg grid place-items-center ${enabled ? "bg-teal-soft text-teal" : "bg-secondary text-muted-foreground"}`}>
          <Sparkle size={16} weight="bold" />
        </div>
        {!enabled && <Lock size={14} weight="bold" className="text-muted-foreground" />}
      </div>
      <div className="mt-3 text-overline text-muted-foreground">{label}</div>
      <div className="mt-1 data-large text-xl">{enabled ? "Included" : "Not included"}</div>
      <div className="mt-1 text-xs text-muted-foreground">{detail}</div>
      {!enabled && (
        <Link to="/pricing?plan=starter" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          Upgrade to unlock →
        </Link>
      )}
    </div>
  );
}
